import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Link, Route } from 'react-router-dom';
import styled, { css } from 'styled-components';
import {
  compose, filter, forEach, get, map,
} from 'lodash/fp';
import { FormattedMessage } from 'react-intl';
import {
  Button, Fade, Snackbar, Tooltip,
} from '@material-ui/core';
import { SaveAlt } from '@material-ui/icons';
import { Grid, Heading } from '../../common/components';
import { hexToRgb } from '../../common/utils';
import * as actions from '../actions';
import {
  PDFDownloadLink, ExportButton, StatusMark, StatusSwitcher, Title,
} from '../components';
import messages from '../messages';

const S = {
  Heading: styled(Heading)`
    margin-right: 16px;
  `,
  Tooltip: styled(({ classes, className, ...otherProps }) => (
    <Tooltip
      className={className}
      classes={{
        ...classes,
        popper: className,
        tooltip: 'tooltip',
      }}
      {...otherProps}
    />
  ))`
    .tooltip {
      position: relative;
      padding: 6px 16px;
      background-color: ${({ theme }) => theme.palette.grey[700]};
    }
    .tooltip::after {
      content: '';
      position: absolute;
      left: 50%;
      bottom: 0;
      transform: translate(-50%, 100%);
      border: 5px solid transparent;
      border-top: 7px solid ${({ theme }) => theme.palette.grey[700]};
    }
  `,
  SaveAlt: styled(SaveAlt)`
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: 1.25rem;
  `,
  StatusMark: styled(StatusMark)`
    order: -1;
    margin-right: 8px;
  `,
  PDFDownloadLink: styled(PDFDownloadLink)`
    margin-left: 4px;
  `,
  Flexbox: styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: 16px;
  `,
  Tabs: styled.div`
    display: flex;
  `,
  Link: styled(({ active, ...otherProps }) => <Button {...otherProps} />).attrs(() => ({ component: Link }))`
    min-width: 100px;
    height: 40px;
    padding: 4px 12px;
    border: 1px solid ${({ theme }) => theme.palette.blueGrey[100]};
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    :first-child {
      border-right: none;
      border-radius: 4px 0 0 4px;
    }
    :last-child {
      border-left: none;
      border-radius: 0 4px 4px 0;
    }
    ${({ active, theme }) => active && css`
      border: none;
      color: ${theme.palette.primary.contrastText};
      background-color: ${theme.palette.primary.light};
      font-weight: 700;
      :hover {
        background-color: ${hexToRgb(theme.palette.primary.light, 0.87)};
      }
    `}
  `,
  Divider: styled.div`
    height: 1px;
    margin-bottom: 16px;
    background-color: ${({ theme }) => theme.palette.grey[300]};
  `,
};

const Newsletter = ({ location, match, routes }) => {
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const activeStatus = useSelector(state => state.newsletter.activeStatus, shallowEqual);
  const newsletter = useSelector(state => state.newsletter.newsletter, shallowEqual);
  const survey = useSelector(state => state.newsletter.survey, shallowEqual);
  const stats = useSelector(state => state.newsletter.stats, shallowEqual);
  const individuals = useSelector(state => state.newsletter.individuals, shallowEqual);

  const dispatch = useDispatch();

  const checked = useMemo(() => get('status', newsletter.data) === 'ongoing', [newsletter.data]);
  const fileUrl = useMemo(() => (get('blob', newsletter.data)
    ? URL.createObjectURL(newsletter.data.blob) : ''), [newsletter.data]);

  const switchActiveStatus = useCallback(() => dispatch(
    actions.switchActiveStatusRequest({ nid: match.params.nid, sid: match.params.sid }),
  ), [dispatch, match.params.nid, match.params.sid]);

  const handleClose = () => setOpen(false);

  const handleEnter = () => {
    const errorType = activeStatus.error ? 'FAIL_TO_SWITCH_STATUS' : 'FAIL_TO_LOAD_NEWSLETTER';

    setErrorMessage(errorType === 'FAIL_TO_SWITCH_STATUS' ? '상태 변경에 실패하였습니다.' : '가정통신문 결과를 불러오는 데 실패하였습니다.');
  };

  const handleExited = () => {
    setErrorMessage(null);
  };

  useEffect(() => {
    dispatch(actions.fetchNewsletterRequest({ nid: match.params.nid, sid: match.params.sid }));
    dispatch(actions.fetchSurveyRequest({ nid: match.params.nid, sid: match.params.sid }));

    return () => {
      dispatch(actions.initReduxNewsletter());
    };
  }, [dispatch, match.params.nid, match.params.sid]);

  useEffect(() => () => URL.revokeObjectURL(fileUrl), [fileUrl]);

  useEffect(() => {
    const hasError = !!(newsletter.error || survey.error || stats.error || activeStatus.error || individuals.error);

    if (hasError) {
      const errors = [
        { value: stats.error, dispatch: () => dispatch(actions.initStats()) },
        { value: individuals.error, dispatch: () => dispatch(actions.initIndividuals()) },
      ];

      const initErrorState = compose(
        forEach(error => error.dispatch()),
        filter(error => error.value),
      );

      setOpen(true);

      initErrorState(errors);
    }
  }, [dispatch, activeStatus.error, newsletter.error, stats.error, survey.error, individuals.error]);

  return (
    <>
      <Grid container spacing={2} padding={{ top: 24, bottom: 72 }}>
        <Grid container item xs={12} margin={{ bottom: 40 }}>
          <S.Heading>
            <FormattedMessage {...messages.newsletterHeading} />
          </S.Heading>
        </Grid>
        <Grid
          container
          item
          xs={12}
          justify="space-between"
          alignItems="center"
          margin={{ bottom: 12 }}
        >
          <Grid container item xs={8} alignItems="center">
            <Title>{get('title', newsletter.data)}</Title>
            <S.StatusMark checked={checked}>
              <FormattedMessage {...messages[`newsletterStatusMark${checked ? 'Ongoing' : 'Closed'}`]} />
            </S.StatusMark>
            <S.PDFDownloadLink newsletter={newsletter} />
          </Grid>
          <Grid>
            <StatusSwitcher
              activeStatus={activeStatus}
              checked={checked}
              onChange={switchActiveStatus}
            />
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <S.Flexbox>
            <S.Tabs>
              <S.Link
                to={{
                  pathname: `/schools/${match.params.sid}/school-newsletters/${match.params.nid}`,
                  state: { from: location.pathname },
                }}
                active={location.pathname === `/schools/${match.params.sid}/school-newsletters/${match.params.nid}`}
              >
                <FormattedMessage {...messages.newsletterLinkStats} />
              </S.Link>
              <S.Link
                to={{
                  pathname: `/schools/${match.params.sid}/school-newsletters/${match.params.nid}/individual`,
                  state: { from: location.pathname },
                }}
                active={location.pathname === `/schools/${match.params.sid}/school-newsletters/${match.params.nid}/individual`}
              >
                <FormattedMessage {...messages.newsletterLinkIndividual} />
              </S.Link>
            </S.Tabs>
            <div>
              <ExportButton
                newsletter={newsletter}
                survey={survey}
                stats={stats}
                individuals={individuals}
              />
            </div>
          </S.Flexbox>
          <S.Divider />
        </Grid>
        {map(({ component: Component, ...routeProps }) => (
          <Route
            {...routeProps}
            render={props => (
              <Component
                {...props}
                fileUrl={fileUrl}
                newsletter={newsletter}
                survey={survey}
              />
            )}
          />
        ), routes)}
      </Grid>
      <Snackbar
        autoHideDuration={3500}
        open={open}
        onClose={handleClose}
        onEnter={handleEnter}
        onExited={handleExited}
        message={errorMessage}
        TransitionComponent={Fade}
      />
    </>
  );
};

Newsletter.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      nid: PropTypes.string.isRequired,
      sid: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  routes: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      component: PropTypes.func.isRequired,
      exact: PropTypes.bool,
      path: PropTypes.string,
    }),
  ),
};

Newsletter.defaultProps = {
  routes: undefined,
};

export default Newsletter;
